<template>
  <div class="api-docs-container">
    <!-- Header -->
    <div class="page-header">
      <h1>API Documentation</h1>
      <div class="header-actions">
        <button @click="generateApiKey" class="btn btn-primary">
          <i class="icon-key"></i> Generate API Key
        </button>
      </div>
    </div>
    
    <!-- Main Content -->
    <div class="main-content">
      <!-- Sidebar -->
      <div class="sidebar">
        <div class="sidebar-section">
          <h3>Quick Start</h3>
          <ul class="nav-list">
            <li><a href="#authentication" @click="scrollTo('authentication')">Authentication</a></li>
            <li><a href="#base-url" @click="scrollTo('base-url')">Base URL</a></li>
            <li><a href="#rate-limits" @click="scrollTo('rate-limits')">Rate Limits</a></li>
          </ul>
        </div>
        
        <div class="sidebar-section">
          <h3>Endpoints</h3>
          <ul class="nav-list">
            <li><a href="#models" @click="scrollTo('models')">Models</a></li>
            <li><a href="#agents" @click="scrollTo('agents')">Agents</a></li>
            <li><a href="#knowledge-base" @click="scrollTo('knowledge-base')">Knowledge Base</a></li>
            <li><a href="#users" @click="scrollTo('users')">Users</a></li>
            <li><a href="#organizations" @click="scrollTo('organizations')">Organizations</a></li>
          </ul>
        </div>
        
        <div class="sidebar-section">
          <h3>SDKs</h3>
          <ul class="nav-list">
            <li><a href="#javascript" @click="scrollTo('javascript')">JavaScript</a></li>
            <li><a href="#python" @click="scrollTo('python')">Python</a></li>
            <li><a href="#curl" @click="scrollTo('curl')">cURL</a></li>
          </ul>
        </div>
      </div>
      
      <!-- Content Area -->
      <div class="content-area">
        <!-- Authentication Section -->
        <section id="authentication" class="doc-section">
          <h2>Authentication</h2>
          <p>The UniPhy API uses API keys for authentication. Include your API key in the Authorization header of your requests.</p>
          
          <div class="code-block">
            <div class="code-header">
              <span>Authorization Header</span>
              <button @click="copyCode('auth-header')" class="copy-btn">Copy</button>
            </div>
            <pre id="auth-header"><code>Authorization: Bearer YOUR_API_KEY</code></pre>
          </div>
          
          <div class="info-box">
            <i class="icon-info"></i>
            <div>
              <strong>Keep your API keys secure!</strong>
              <p>Never expose your API keys in client-side code or public repositories.</p>
            </div>
          </div>
        </section>
        
        <!-- Base URL Section -->
        <section id="base-url" class="doc-section">
          <h2>Base URL</h2>
          <p>All API requests should be made to the following base URL:</p>
          
          <div class="code-block">
            <div class="code-header">
              <span>Base URL</span>
              <button @click="copyCode('base-url')" class="copy-btn">Copy</button>
            </div>
            <pre id="base-url"><code>https://api.uniphy.com/v1</code></pre>
          </div>
        </section>
        
        <!-- Rate Limits Section -->
        <section id="rate-limits" class="doc-section">
          <h2>Rate Limits</h2>
          <p>The API enforces rate limits to ensure fair usage. Current limits are:</p>
          
          <div class="rate-limits-table">
            <table>
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Requests per minute</th>
                  <th>Requests per hour</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Free</td>
                  <td>60</td>
                  <td>1,000</td>
                </tr>
                <tr>
                  <td>Pro</td>
                  <td>300</td>
                  <td>10,000</td>
                </tr>
                <tr>
                  <td>Enterprise</td>
                  <td>1,000</td>
                  <td>50,000</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        
        <!-- Models Section -->
        <section id="models" class="doc-section">
          <h2>Models</h2>
          <p>Manage LLM models in your organization.</p>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method get">GET</span>
              <span class="path">/models</span>
            </div>
            <p>List all available models</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Request</span>
                <button @click="copyCode('models-get')" class="copy-btn">Copy</button>
              </div>
              <pre id="models-get"><code>curl -X GET "https://api.uniphy.com/v1/models" \
  -H "Authorization: Bearer YOUR_API_KEY"</code></pre>
            </div>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Response</span>
                <button @click="copyCode('models-response')" class="copy-btn">Copy</button>
              </div>
              <pre id="models-response"><code>{
  "models": [
    {
      "id": "gpt-4",
      "name": "GPT-4",
      "provider": "openai",
      "type": "chat",
      "status": "active",
      "created_at": "2024-01-01T00:00:00Z"
    }
  ],
  "total": 1,
  "page": 1,
  "per_page": 20
}</code></pre>
            </div>
          </div>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method post">POST</span>
              <span class="path">/models</span>
            </div>
            <p>Create a new model</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Request</span>
                <button @click="copyCode('models-post')" class="copy-btn">Copy</button>
              </div>
              <pre id="models-post"><code>curl -X POST "https://api.uniphy.com/v1/models" \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "name": "My Custom Model",
    "provider": "openai",
    "model_id": "gpt-4-turbo",
    "type": "chat",
    "config": {
      "temperature": 0.7,
      "max_tokens": 4000
    }
  }'</code></pre>
            </div>
          </div>
        </section>
        
        <!-- Agents Section -->
        <section id="agents" class="doc-section">
          <h2>Agents</h2>
          <p>Create and manage AI agents with specific capabilities.</p>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method get">GET</span>
              <span class="path">/agents</span>
            </div>
            <p>List all agents</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Response</span>
                <button @click="copyCode('agents-response')" class="copy-btn">Copy</button>
              </div>
              <pre id="agents-response"><code>{
  "agents": [
    {
      "id": "agent-123",
      "name": "Customer Support Agent",
      "description": "Handles customer inquiries",
      "model_id": "gpt-4",
      "knowledge_base_ids": ["kb-456"],
      "status": "active",
      "created_at": "2024-01-01T00:00:00Z"
    }
  ]
}</code></pre>
            </div>
          </div>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method post">POST</span>
              <span class="path">/agents/{agent_id}/chat</span>
            </div>
            <p>Send a message to an agent</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Request</span>
                <button @click="copyCode('agent-chat')" class="copy-btn">Copy</button>
              </div>
              <pre id="agent-chat"><code>curl -X POST "https://api.uniphy.com/v1/agents/agent-123/chat" \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "message": "How can I reset my password?",
    "session_id": "session-789"
  }'</code></pre>
            </div>
          </div>
        </section>
        
        <!-- Knowledge Base Section -->
        <section id="knowledge-base" class="doc-section">
          <h2>Knowledge Base</h2>
          <p>Manage knowledge bases and documents.</p>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method post">POST</span>
              <span class="path">/knowledge-bases/{kb_id}/documents</span>
            </div>
            <p>Upload a document to a knowledge base</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Request</span>
                <button @click="copyCode('kb-upload')" class="copy-btn">Copy</button>
              </div>
              <pre id="kb-upload"><code>curl -X POST "https://api.uniphy.com/v1/knowledge-bases/kb-456/documents" \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -F "file=@document.pdf" \
  -F "metadata={\"title\":\"User Manual\",\"category\":\"documentation\"}"</code></pre>
            </div>
          </div>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method get">GET</span>
              <span class="path">/knowledge-bases/{kb_id}/search</span>
            </div>
            <p>Search within a knowledge base</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Request</span>
                <button @click="copyCode('kb-search')" class="copy-btn">Copy</button>
              </div>
              <pre id="kb-search"><code>curl -X GET "https://api.uniphy.com/v1/knowledge-bases/kb-456/search?q=password+reset" \
  -H "Authorization: Bearer YOUR_API_KEY"</code></pre>
            </div>
          </div>
        </section>
        
        <!-- Users Section -->
        <section id="users" class="doc-section">
          <h2>Users</h2>
          <p>Manage users within your organization.</p>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method get">GET</span>
              <span class="path">/users</span>
            </div>
            <p>List all users in your organization</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Response</span>
                <button @click="copyCode('users-response')" class="copy-btn">Copy</button>
              </div>
              <pre id="users-response"><code>{
  "users": [
    {
      "id": "user-123",
      "email": "john@example.com",
      "name": "John Doe",
      "role_id": "role-456",
      "is_active": true,
      "last_login": "2024-01-15T10:30:00Z",
      "created_at": "2024-01-01T00:00:00Z"
    }
  ],
  "total": 1,
  "page": 1,
  "per_page": 20
}</code></pre>
            </div>
          </div>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method post">POST</span>
              <span class="path">/users/invite</span>
            </div>
            <p>Invite a new user to your organization</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Request</span>
                <button @click="copyCode('users-invite')" class="copy-btn">Copy</button>
              </div>
              <pre id="users-invite"><code>curl -X POST "https://api.uniphy.com/v1/users/invite" \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "email": "newuser@example.com",
    "role_id": "role-456",
    "send_email": true
  }'</code></pre>
            </div>
          </div>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method put">PUT</span>
              <span class="path">/users/{user_id}/role</span>
            </div>
            <p>Update a user's role</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Request</span>
                <button @click="copyCode('users-role')" class="copy-btn">Copy</button>
              </div>
              <pre id="users-role"><code>curl -X PUT "https://api.uniphy.com/v1/users/user-123/role" \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "role_id": "role-789"
  }'</code></pre>
            </div>
          </div>
        </section>
        
        <!-- Organizations Section -->
        <section id="organizations" class="doc-section">
          <h2>Organizations</h2>
          <p>Manage organization settings and information.</p>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method get">GET</span>
              <span class="path">/organization</span>
            </div>
            <p>Get current organization details</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Response</span>
                <button @click="copyCode('org-response')" class="copy-btn">Copy</button>
              </div>
              <pre id="org-response"><code>{
  "organization": {
    "id": "org-123",
    "name": "Acme Corp",
    "description": "Leading technology company",
    "industry": "technology",
    "size": "51-200",
    "email": "contact@acme.com",
    "website": "https://acme.com",
    "timezone": "America/New_York",
    "settings": {
      "allowPublicAccess": false,
      "enableAnalytics": true
    },
    "created_at": "2024-01-01T00:00:00Z",
    "updated_at": "2024-01-15T10:30:00Z"
  }
}</code></pre>
            </div>
          </div>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method put">PUT</span>
              <span class="path">/organization</span>
            </div>
            <p>Update organization information</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Request</span>
                <button @click="copyCode('org-update')" class="copy-btn">Copy</button>
              </div>
              <pre id="org-update"><code>curl -X PUT "https://api.uniphy.com/v1/organization" \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "name": "Acme Corporation",
    "description": "Leading AI technology company",
    "industry": "technology",
    "email": "info@acme.com",
    "website": "https://acme.com",
    "timezone": "America/New_York"
  }'</code></pre>
            </div>
          </div>
          
          <div class="endpoint">
            <div class="endpoint-header">
              <span class="method get">GET</span>
              <span class="path">/organization/usage</span>
            </div>
            <p>Get organization usage statistics</p>
            
            <div class="code-block">
              <div class="code-header">
                <span>Example Response</span>
                <button @click="copyCode('org-usage')" class="copy-btn">Copy</button>
              </div>
              <pre id="org-usage"><code>{
  "usage": {
    "current_period": {
      "start_date": "2024-01-01",
      "end_date": "2024-01-31",
      "api_requests": 15847,
      "model_tokens": 2847592,
      "storage_used_mb": 1248,
      "active_users": 12
    },
    "limits": {
      "api_requests_per_month": 100000,
      "model_tokens_per_month": 10000000,
      "storage_limit_gb": 50,
      "max_users": 50
    },
    "billing_tier": "pro"
  }
}</code></pre>
            </div>
          </div>
        </section>
        
        <!-- Error Handling Section -->
        <section id="errors" class="doc-section">
          <h2>Error Handling</h2>
          <p>The API uses conventional HTTP response codes to indicate success or failure.</p>
          
          <div class="error-codes-table">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>200</td>
                  <td>OK</td>
                  <td>Request was successful</td>
                </tr>
                <tr>
                  <td>201</td>
                  <td>Created</td>
                  <td>Resource was created successfully</td>
                </tr>
                <tr>
                  <td>400</td>
                  <td>Bad Request</td>
                  <td>Request was invalid or malformed</td>
                </tr>
                <tr>
                  <td>401</td>
                  <td>Unauthorized</td>
                  <td>API key is missing or invalid</td>
                </tr>
                <tr>
                  <td>403</td>
                  <td>Forbidden</td>
                  <td>API key doesn't have required permissions</td>
                </tr>
                <tr>
                  <td>404</td>
                  <td>Not Found</td>
                  <td>Requested resource doesn't exist</td>
                </tr>
                <tr>
                  <td>429</td>
                  <td>Too Many Requests</td>
                  <td>Rate limit exceeded</td>
                </tr>
                <tr>
                  <td>500</td>
                  <td>Internal Server Error</td>
                  <td>Something went wrong on our end</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="code-block">
            <div class="code-header">
              <span>Error Response Format</span>
              <button @click="copyCode('error-format')" class="copy-btn">Copy</button>
            </div>
            <pre id="error-format"><code>{
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "The request body is invalid",
    "details": [
      {
        "field": "email",
        "message": "Email is required"
      }
    ]
  }
}</code></pre>
          </div>
        </section>
        
        <!-- JavaScript SDK Section -->
        <section id="javascript" class="doc-section">
          <h2>JavaScript SDK</h2>
          <p>Use our JavaScript SDK for easy integration.</p>
          
          <div class="code-block">
            <div class="code-header">
              <span>Installation</span>
              <button @click="copyCode('js-install')" class="copy-btn">Copy</button>
            </div>
            <pre id="js-install"><code>npm install @uniphy/sdk</code></pre>
          </div>
          
          <div class="code-block">
            <div class="code-header">
              <span>Usage</span>
              <button @click="copyCode('js-usage')" class="copy-btn">Copy</button>
            </div>
            <pre id="js-usage"><code>import { UniPhyClient } from '@uniphy/sdk';

const client = new UniPhyClient({
  apiKey: 'YOUR_API_KEY'
});

// List models
const models = await client.models.list();

// Chat with an agent
const response = await client.agents.chat('agent-123', {
  message: 'Hello, how can you help me?'
});</code></pre>
          </div>
        </section>
        
        <!-- Python SDK Section -->
        <section id="python" class="doc-section">
          <h2>Python SDK</h2>
          <p>Use our Python SDK for server-side integration.</p>
          
          <div class="code-block">
            <div class="code-header">
              <span>Installation</span>
              <button @click="copyCode('py-install')" class="copy-btn">Copy</button>
            </div>
            <pre id="py-install"><code>pip install uniphy</code></pre>
          </div>
          
          <div class="code-block">
            <div class="code-header">
              <span>Usage</span>
              <button @click="copyCode('py-usage')" class="copy-btn">Copy</button>
            </div>
            <pre id="py-usage"><code>from uniphy import UniPhyClient

client = UniPhyClient(api_key="YOUR_API_KEY")

# List models
models = client.models.list()

# Chat with an agent
response = client.agents.chat(
    agent_id="agent-123",
    message="Hello, how can you help me?"
)</code></pre>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'ApiDocView',
  setup() {
    const scrollTo = (elementId) => {
      const element = document.getElementById(elementId)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }

    const copyCode = (elementId) => {
      const element = document.getElementById(elementId)
      if (element) {
        const text = element.textContent || element.innerText
        navigator.clipboard.writeText(text)
        
        // Show feedback
        const button = element.parentElement.querySelector('.copy-btn')
        if (button) {
          const originalText = button.textContent
          button.textContent = 'Copied!'
          button.style.background = '#51cf66'
          setTimeout(() => {
            button.textContent = originalText
            button.style.background = ''
          }, 2000)
        }
      }
    }

    const generateApiKey = () => {
      // This would typically open a modal or redirect to settings
      alert('This would generate an API key. For now, go to Settings to manage API keys.')
    }

    return {
      scrollTo,
      copyCode,
      generateApiKey
    }
  }
}
</script>

<style scoped>
.api-docs-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.page-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

/* Main Content */
.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Sidebar */
.sidebar {
  width: 280px;
  background: white;
  border-right: 1px solid #e9ecef;
  overflow-y: auto;
  padding: 1.5rem 0;
}

.sidebar-section {
  margin-bottom: 2rem;
  padding: 0 1.5rem;
}

.sidebar-section h3 {
  margin: 0 0 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-list li {
  margin-bottom: 0.5rem;
}

.nav-list a {
  display: block;
  padding: 0.5rem 0.75rem;
  color: #6c757d;
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 0.9rem;
}

.nav-list a:hover {
  color: #667eea;
  background: #f8f9fa;
}

/* Content Area */
.content-area {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  background: white;
}

/* Documentation Sections */
.doc-section {
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e9ecef;
}

.doc-section:last-child {
  border-bottom: none;
}

.doc-section h2 {
  margin: 0 0 1rem;
  font-size: 1.75rem;
  font-weight: 600;
  color: #2c3e50;
}

.doc-section p {
  margin: 0 0 1.5rem;
  line-height: 1.6;
  color: #495057;
}

/* Code Blocks */
.code-block {
  margin: 1.5rem 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-weight: 500;
  color: #495057;
}

.copy-btn {
  padding: 0.25rem 0.75rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s;
}

.copy-btn:hover {
  background: #5a6fd8;
}

pre {
  margin: 0;
  padding: 1rem;
  background: #2d3748;
  color: #e2e8f0;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  overflow-x: auto;
}

code {
  font-family: 'Courier New', monospace;
}

/* Endpoints */
.endpoint {
  margin: 2rem 0;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.endpoint-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.method {
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.method.get {
  background: #51cf66;
  color: white;
}

.method.post {
  background: #339af0;
  color: white;
}

.method.put {
  background: #ffd43b;
  color: #495057;
}

.method.delete {
  background: #ff6b6b;
  color: white;
}

.path {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #495057;
}

/* Tables */
.rate-limits-table {
  margin: 1.5rem 0;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

th, td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
}

th {
  background: #667eea;
  color: white;
  font-weight: 600;
}

tr:hover {
  background: #f8f9fa;
}

/* Info Box */
.info-box {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: #e3f2fd;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
  margin: 1.5rem 0;
}

.info-box .icon-info {
  color: #2196f3;
  font-size: 1.25rem;
  margin-top: 0.125rem;
}

.info-box strong {
  display: block;
  margin-bottom: 0.5rem;
  color: #1976d2;
}

.info-box p {
  margin: 0;
  color: #1565c0;
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* Icons */
.icon-key:before { content: '🔑'; }
.icon-info:before { content: 'ℹ️'; }

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
    max-height: 200px;
    border-right: none;
    border-bottom: 1px solid #e9ecef;
  }
  
  .content-area {
    padding: 1rem;
  }
  
  .endpoint-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}

/* Smooth scroll */
html {
  scroll-behavior: smooth;
}
</style>

